/**
 * supabaseData.js — Supabase CRUD helpers for all persistent data.
 * Every function expects a supabase client instance + the current user_id.
 */
import { supabase } from './supabaseClient';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function userId() {
  return supabase?.auth.getUser().then(r => r?.data?.user?.id);
}

// ─── Settings ─────────────────────────────────────────────────────────────────
//
// Efter V20-migrationen:
//   - brand_guidelines + categories → GLOBALA via app_settings-tabellen (singleton)
//   - model + vocab_collections → kvar per-user i settings-tabellen
//
// fetchSettings returnerar en sammanslagen vy så att DataContext-koden inte
// behöver känna till delningen.
export async function fetchSettings(uid) {
  if (!supabase) return null;
  // Globala värden
  let brand_guidelines = '';
  let categories = null;
  try {
    const { data } = await supabase
      .from('app_settings')
      .select('brand_guidelines, categories')
      .eq('id', 1)
      .maybeSingle();
    if (data) {
      brand_guidelines = data.brand_guidelines || '';
      categories = data.categories || null;
    }
  } catch (_) { /* app_settings kan saknas pre-V20 — fallback nedan */ }

  // Per-user värden (model + vocab_collections)
  let model = null;
  let vocab_collections = null;
  let preMigrationBG = null;
  let preMigrationCats = null;
  try {
    const { data } = await supabase
      .from('settings')
      .select('brand_guidelines, model, vocab_collections, categories')
      .eq('user_id', uid)
      .maybeSingle();
    if (data) {
      model = data.model;
      vocab_collections = data.vocab_collections;
      preMigrationBG   = data.brand_guidelines;
      preMigrationCats = data.categories;
    }
  } catch (_) {
    try {
      const { data } = await supabase
        .from('settings')
        .select('brand_guidelines, model')
        .eq('user_id', uid)
        .maybeSingle();
      if (data) { model = data.model; preMigrationBG = data.brand_guidelines; }
    } catch (__) { /* ignore */ }
  }

  return {
    brand_guidelines: brand_guidelines || preMigrationBG || '',
    categories:       categories       || preMigrationCats || null,
    model:            model,
    vocab_collections,
  };
}

export async function upsertVocabCollections(uid, collections) {
  if (!supabase) return;
  await supabase.from('settings').upsert(
    { user_id: uid, vocab_collections: collections, updated_at: new Date().toISOString() },
    { onConflict: 'user_id' }
  );
}

// Brand guidelines är nu globalt: skriver mot app_settings (id=1).
export async function upsertSettings(uid, brandGuidelines) {
  if (!supabase) return;
  await supabase.from('app_settings').upsert(
    { id: 1, brand_guidelines: brandGuidelines, last_edited_by: uid },
    { onConflict: 'id' }
  );
}

export async function upsertModel(uid, model) {
  if (!supabase) return;
  await supabase.from('settings').upsert(
    { user_id: uid, model, updated_at: new Date().toISOString() },
    { onConflict: 'user_id' }
  );
}

// Kategorier är nu globala: läses från app_settings (matchar fetchSettings).
export async function fetchCategories(_uid) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('app_settings')
    .select('categories')
    .eq('id', 1)
    .maybeSingle();
  if (error) return null;
  return data?.categories || null;
}

export async function upsertCategories(uid, categories) {
  if (!supabase) return;
  await supabase.from('app_settings').upsert(
    { id: 1, categories, last_edited_by: uid },
    { onConflict: 'id' }
  );
}

// ─── Templates ────────────────────────────────────────────────────────────────
// Efter V20: globalt delade per kategori (ingen filter på user_id).
export async function fetchTemplates(_uid) {
  if (!supabase) return null;
  const { data } = await supabase
    .from('templates')
    .select('category, content');
  if (!data) return null;
  return data.reduce((acc, row) => { acc[row.category] = row.content; return acc; }, {});
}

export async function upsertTemplates(uid, categoryTemplates) {
  if (!supabase || !categoryTemplates) return;
  const nonEmpty = Object.entries(categoryTemplates)
    .filter(([, content]) => content && content.trim().length > 0)
    .map(([category, content]) => ({ user_id: uid, category, content }));
  const cleared = Object.entries(categoryTemplates)
    .filter(([, content]) => !content || content.trim().length === 0)
    .map(([category]) => category);
  // Globalt delade (V20): per-rad upsert på `category` (unique constraint templates_category_unique)
  // istället för delete-all-then-insert — annars kan samtidiga användare skriva över varandras
  // tillägg. Om en användare medvetet rensar en malls innehåll → radera bara den specifika kategorin.
  if (nonEmpty.length > 0) {
    const { error } = await supabase.from('templates').upsert(nonEmpty, { onConflict: 'category' });
    if (error) throw error;
  }
  if (cleared.length > 0) {
    const { error } = await supabase.from('templates').delete().in('category', cleared);
    if (error) throw error;
  }
}

export async function deleteTemplateByCategory(category) {
  if (!supabase || !category) return;
  const { error } = await supabase.from('templates').delete().eq('category', category);
  if (error) throw error;
}

// ─── Vocabulary ───────────────────────────────────────────────────────────────
// Efter V20: globalt delad ordlista.
export async function fetchVocabulary(_uid) {
  if (!supabase) return null;
  const { data } = await supabase
    .from('vocabulary')
    .select('*')
    .order('created_at', { ascending: false });
  return data || [];
}

export async function upsertVocabularyAll(uid, entries) {
  if (!supabase) return;
  // Globalt delad: replace strategy ersätter ALLA rader (inte bara user's).
  await supabase.from('vocabulary').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  if (!entries.length) return;
  const rows = entries.map(e => ({
    user_id: uid,  // last_edited_by efter V20
    term_se: e.termSE || e.term_se || e.term || '',
    category: e.category || null,
    translations: e.translations || {},
    status: e.status || 'Pending',
    notes: e.notes || '',
    collection: e.collection || 'Standard',
    created_at: e.createdAt || e.created_at || new Date().toISOString(),
  }));
  await supabase.from('vocabulary').insert(rows);
}

// ─── Logs ─────────────────────────────────────────────────────────────────────
export async function fetchLogs(uid) {
  if (!supabase) return null;
  const { data } = await supabase
    .from('logs')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false })
    .limit(500);
  return data || [];
}

export async function insertLog(uid, entry) {
  if (!supabase) return;
  await supabase.from('logs').insert({
    user_id: uid,
    type: entry.type || null,
    file_name: entry.fileName || null,
    product_name: entry.productName || null,
    markets: entry.markets || [],
    products: entry.products || 1,
    success: entry.success || 0,
    failed: entry.failed || 0,
    duration_secs: entry.durationSeconds || entry.duration_secs || null,
    per_product: entry.perProduct || null,
    output_data: entry.outputData || null,
    created_at: new Date().toISOString(),
  });
}

export async function deleteLogs(uid) {
  if (!supabase) return;
  await supabase.from('logs').delete().eq('user_id', uid);
}

// ─── Favorites ────────────────────────────────────────────────────────────────
export async function fetchFavorites(uid) {
  if (!supabase) return null;
  const { data } = await supabase
    .from('favorites')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  return data || [];
}

export async function insertFavorite(uid, entry) {
  if (!supabase) return null;
  const { data } = await supabase.from('favorites').insert({
    user_id: uid,
    product_name: entry.productName || entry.product_name || '',
    category: entry.category || '',
    market: entry.market || '',
    output: entry.output || '',
    word_count: entry.wordCount || entry.word_count || 0,
    features: entry.features || '',
    meta_title: entry.metaTitle || entry.meta_title || '',
    meta_description: entry.metaDescription || entry.meta_description || '',
    created_at: new Date().toISOString(),
  }).select().single();
  return data;
}

export async function deleteFavorite(uid, id) {
  if (!supabase) return;
  await supabase.from('favorites').delete().eq('user_id', uid).eq('id', id);
}

export async function updateFavorite(uid, id, updates) {
  if (!supabase) return;
  await supabase.from('favorites').update({
    output: updates.output || '',
    meta_title: updates.metaTitle || '',
    meta_description: updates.metaDescription || '',
  }).eq('user_id', uid).eq('id', id);
}

// ─── History ──────────────────────────────────────────────────────────────────
export async function fetchHistory(_uid) {
  if (!supabase) return null;
  // Efter V21 är history globalt läsbar för alla aktiva användare —
  // ignorera uid och hämta hela teamets historik. Paginera i 1000-block.
  const PAGE = 1000;
  let from = 0;
  const all = [];
  for (;;) {
    const { data, error } = await supabase
      .from('history')
      .select('*')
      .order('created_at', { ascending: false })
      .range(from, from + PAGE - 1);
    if (error) return null;
    const batch = data || [];
    all.push(...batch);
    if (batch.length < PAGE) break;
    from += PAGE;
    if (from > 100_000) break;
  }
  return all;
}

export async function insertHistoryEntry(uid, entry) {
  if (!supabase) return;
  await supabase.from('history').insert({
    user_id: uid,
    product_name: entry.productName || entry.product_name || '',
    category: entry.category || null,
    market: entry.market || null,
    word_count: entry.wordCount || entry.word_count || 0,
    source: entry.source || 'single',
    created_at: entry.timestamp || new Date().toISOString(),
  });
}

export async function deleteHistory(uid) {
  if (!supabase) return;
  await supabase.from('history').delete().eq('user_id', uid);
}

// ─── Translation History ──────────────────────────────────────────────────────
export async function fetchTranslationHistory(uid) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('translation_history')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) return null; // table may not exist yet — fail silently
  return data || [];
}

export async function insertTranslationHistoryEntry(uid, entry) {
  if (!supabase) return;
  await supabase.from('translation_history').insert({
    user_id: uid,
    snippet: (entry.snippet || '').slice(0, 60),
    markets: entry.markets || [],
    source_lang: entry.sourceLang || entry.source_lang || null,
    full_text: entry.fullText || null,
    results_json: entry.results || null,
    created_at: entry.timestamp || new Date().toISOString(),
  });
}

export async function deleteTranslationHistory(uid) {
  if (!supabase) return;
  await supabase.from('translation_history').delete().eq('user_id', uid);
}
// ─── Usage / Cost Tracking ─────────────────────────────────────────────────────

// Pricing per 1M tokens (USD) — Google/Anthropic/OpenAI public pricing Feb 2026
const PRICING = {
  gemini: { input: 0.30, output: 2.50 },  // Gemini 2.5 Flash
  claude: { input: 3.0,  output: 15.0 },  // Claude Sonnet 4.5
  gpt4o:  { input: 2.50, output: 10.0 },  // GPT-4o
};

export function estimateCost(model, inputTokens, outputTokens) {
  const p = PRICING[model] || PRICING.gemini;
  return (inputTokens / 1_000_000) * p.input + (outputTokens / 1_000_000) * p.output;
}

export async function logUsage(uid, { model, inputTokens, outputTokens, market, category, source, module, userEmail }) {
  if (!supabase || !uid) return;
  const estimated_cost_usd = estimateCost(model, inputTokens, outputTokens);
  try {
    await supabase.from('usage').insert({
      user_id: uid,
      user_email: userEmail || null,
      model: model || 'gemini',
      module: module || null,
      input_tokens: inputTokens || 0,
      output_tokens: outputTokens || 0,
      estimated_cost_usd,
      market: market || null,
      category: category || null,
      source: source || null,
    });
  } catch (_) { /* table may not exist pre-migration — fail silently */ }
}

// PostgREST har default max-rows=1000 per request — paginera i 1000-block tills tomt.
async function _fetchUsagePaginated(applyFilters) {
  if (!supabase) return null;
  const PAGE = 1000;
  let from = 0;
  const all = [];
  try {
    for (;;) {
      const q = applyFilters(
        supabase.from('usage').select('model, module, user_email, input_tokens, output_tokens, estimated_cost_usd, market, category, source, created_at')
      ).order('created_at', { ascending: false }).range(from, from + PAGE - 1);
      const { data, error } = await q;
      if (error) return null;
      const batch = data || [];
      all.push(...batch);
      if (batch.length < PAGE) break;
      from += PAGE;
      if (from > 100_000) break; // hard-stop, undvik oändlig loop
    }
    return all;
  } catch (_) { return null; }
}

export async function fetchUsage(uid, year, month) {
  if (!supabase || !uid) return null;
  const fromD = `${year}-${String(month).padStart(2,'0')}-01`;
  const toMonth = month === 12 ? `${year+1}-01-01` : `${year}-${String(month+1).padStart(2,'0')}-01`;
  return _fetchUsagePaginated(q => q.eq('user_id', uid).gte('created_at', fromD).lt('created_at', toMonth));
}

// Admin: hämta ALLA användares usage. RLS-policy "Admins read all usage" filtrerar serverside.
export async function fetchAllUsage(year, month) {
  if (!supabase) return null;
  const fromD = `${year}-${String(month).padStart(2,'0')}-01`;
  const toMonth = month === 12 ? `${year+1}-01-01` : `${year}-${String(month+1).padStart(2,'0')}-01`;
  return _fetchUsagePaginated(q => q.gte('created_at', fromD).lt('created_at', toMonth));
}

// Admin: hämta ALLA användares usage för ett godtyckligt datumintervall (ISO-strängar).
// Används av Cost Dashboard för "Senaste 30 dagar".
export async function fetchAllUsageRange(fromIso, toIso) {
  if (!supabase) return null;
  return _fetchUsagePaginated(q => q.gte('created_at', fromIso).lt('created_at', toIso));
}
